import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../../stores/root';

// assets
import { faHome, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../services/general/error';
import VesselService from '../../../../services/vessels/vessel';

// props
import { IVesselResourceProps } from '../../../../props/vessels/vessel';

// components
import { ActionButton, CommandBar, DefaultButton, ICommandBarItemProps, PrimaryButton, Stack } from '@fluentui/react';
import DetailsStack, { IStackSectionProps } from '../../../uiframeworks/detailsStack';
import LoadingComponent from '../../../feedbacks/loading';
import NoAccess from '../../../uiframeworks/noAccess';
import PermissionsService from '../../../../services/permissions';
import VesselDetailsProperties from './properties';
import VesselForm from '../form';
import VesselEnginesList from '../../engines/general/list';
import VesselEngineForm from '../../engines/general/form';
import VesselCrewsList from '../../crews/general/list';
import VesselDocumentsList from './documents';

interface VesselDetailsProps {
    vesselId: string;
}

const VesselDetails: React.FC<VesselDetailsProps> = observer((props: VesselDetailsProps) => {
    const { banner, topbar, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [vessel, setVessel] = React.useState<IVesselResourceProps | undefined>();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [hasPermission, setHasPermission] = React.useState<boolean>(false);

    useEffect(() => {
        _onGetVessel();
    }, []);

    const _onGetVessel = async () => {
        try {
            setLoaded(false);

            const hasPermission = PermissionsService.hasPermission(['vessels.read.all'], user.permissions);
            setHasPermission(hasPermission);

            const _vessel = await VesselService.read(props.vesselId);
            setVessel(_vessel);

            topbar.show(_vessel.name, [
                { key: "", icon: faHome, href: "/" },
                { key: 'vessels', href: "/vessels", text: 'Vessels' },
                { key: props.vesselId, text: _vessel.name }
            ]);

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_vessel_error',
                text: 'Failed to get vessel details. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        items.push({
            key: 'update',
            text: 'Update details',
            onRender: () => {
                return <PrimaryButton text={'Update details'}
                    iconProps={{ iconName: "Edit" }}
                    onClick={() => setActiveSurface('vessel.update')} />
            }
        });

        items.push({
            key: 'download',
            text: 'Download Ship Particular',
            onRender: () => <DefaultButton text={'Download Ship Particular'} iconProps={{ iconName: "Download" }} onClick={() => { }} styles={{ root: { marginLeft: 10 } }} />
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        if (refresh) { _onGetVessel() }
    }

    const leftSections = (_vessel: IVesselResourceProps): IStackSectionProps[] => {
        return [
            {
                key: 'properties',
                header: { title: 'Vessel Details' },
                isCard: true,
                content: <VesselDetailsProperties vessel={_vessel} />
            },
            /*{
                key: 'secondrow',
                subsections: [
                    {
                        key: 'commissions',
                        width: '50%',
                        header: { title: 'Commissions Breakdown' },
                        isCard: true,
                        content: <EmployeeContractDetailsCommissions contract={_contract} />
                    },
                    {
                        key: 'placeholder'
                    }
                ]
            }*/
        ];
    }

    const tabSections = (_vessel: IVesselResourceProps): IStackSectionProps[] => {
        return [
            {
                key: 'tabs',
                isCard: true,
                tabs: [
                    {
                        key: 'crews',
                        title: 'Crews List',
                        content: <VesselCrewsList vesselId={_vessel.id} />
                    },
                    {
                        key: 'documents',
                        title: 'Documents',
                        content: <VesselDocumentsList vesselId={_vessel.id} />
                    }
                ]
            }
            /*{
                key: 'secondrow',
                subsections: [
                    {
                        key: 'commissions',
                        width: '50%',
                        header: { title: 'Commissions Breakdown' },
                        isCard: true,
                        content: <EmployeeContractDetailsCommissions contract={_contract} />
                    },
                    {
                        key: 'placeholder'
                    }
                ]
            }*/
        ];
    }

    const rightSections = (_vessel: IVesselResourceProps): IStackSectionProps[] => {
        const engineSection: IStackSectionProps = {
            key: 'engines',
            header: {
                title: 'Engines',
                buttons: []
            },
            isCard: true,
            content: <VesselEnginesList vesselId={props.vesselId} variant={'plain'}
                hideCommandBar hideCreateButton hideFilter hideQuickFilter hidePagination
                columns={['name', 'type', 'actions']} />
        };

        const craneSection: IStackSectionProps = {
            key: 'cranes',
            header: {
                title: 'Cranes',
                buttons: []
            },
            isCard: true,
            content: <Stack></Stack>
        };

        if (PermissionsService.hasPermission(['vessels.update.all'], user.permissions)) {
            engineSection.header?.buttons?.push({ text: "Add engine", iconProps: { iconName: "Add" }, onClick: () => setActiveSurface('engine.create') });
            craneSection.header?.buttons?.push({ text: "Add crane", iconProps: { iconName: "Add" } });
        }

        return [engineSection, craneSection];
    }

    return <Stack tokens={{ childrenGap: 20 }}>
        {!loaded ? <LoadingComponent label='Retrieving vessel details ...' labelPosition='right' spinnerPosition='baseline' /> : null}
        {loaded && hasPermission && vessel ? <>
            <CommandBar
                items={getCommandBarItems().items}
                farItems={getCommandBarItems().farItems}
                styles={{
                    root: {
                        padding: 0,
                        height: 'unset',
                        backgroundColor: 'transparent'
                    }
                }}
                ariaLabel="Use left and right arrow keys to navigate between commands" />
            <Stack tokens={{ childrenGap: 20 }}>
                <DetailsStack left={leftSections(vessel)} right={rightSections(vessel)} rightWidth={450} />
                <DetailsStack left={tabSections(vessel)} />
            </Stack>
        </> : null}
        {loaded && !hasPermission ? <NoAccess /> : null}
        {activeSurface === 'vessel.update' ? <VesselForm onDismissed={_onSurfaceDismissed} vesselId={props.vesselId} /> : null}
        {activeSurface === 'engine.create' ? <VesselEngineForm onDismissed={_onSurfaceDismissed} vesselId={props.vesselId} /> : null}
    </Stack>;
});

export default VesselDetails;
